import { Locale } from '@headless-workspace/config';
import {
    PreviewOptions,
    Repository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import { RequestStrategy } from '@headless-workspace/data';
import { DecoderHelpers } from '@headless-workspace/utils';
import { VipBrandsValue } from '../../config';
import { RecommendedProductsWithUnknownDTO, RecommendedTileProductDTO } from './RecommendedProductsDTO';
import { mapRecommendedProducts, RecommendedProductsValue } from './RecommendedProductsValue';

type GetProductRecommendationParams = PreviewOptions & {
    locale: Locale;
    productId?: string;
    pageId: string;
    vipBrands: VipBrandsValue;
    deviceId?: string;
    originalPriceText?: string;
};

export interface RecommendedProductsRepositorySpec {
    getRecommendedProducts({
        locale,
        productId,
        pageId,
        vipBrands,
        deviceId,
        originalPriceText,
        isPreviewable,
    }: GetProductRecommendationParams): Promise<Result<RecommendedProductsValue[]>>;
}

export class RecommendedProductsRepository extends Repository implements RecommendedProductsRepositorySpec {
    readonly path = '/catalog/v1';

    constructor(requestStrategy: RequestStrategy) {
        super(requestStrategy.authStrategy, requestStrategy.refreshTokenStrategy, requestStrategy.queryParamStrategy);
    }

    async getRecommendedProducts({
        locale,
        productId,
        pageId,
        vipBrands,
        deviceId,
        originalPriceText,
        isPreviewable = false,
    }: GetProductRecommendationParams): Promise<Result<RecommendedProductsValue[]>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/recommendations`, {
                locale,
                pageId,
                ...(deviceId && { deviceId }),
                ...(productId && { productId }),
                isPreviewable,
            });
            const recommendationContainer = RecommendedProductsWithUnknownDTO.parse(response);

            const validRecommendedProducts = recommendationContainer.recommendations.flatMap((recommendation) => {
                const products = DecoderHelpers.safeParseArray(recommendation.products, RecommendedTileProductDTO);

                if (products.length === 0) return [];

                return [
                    {
                        id: recommendation.id,
                        title: recommendation.title,
                        cta: recommendation.cta,
                        type: recommendation.type,
                        products,
                    },
                ];
            });

            if (validRecommendedProducts.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(mapRecommendedProducts(validRecommendedProducts, vipBrands, originalPriceText));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
